import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Users, Building2, BadgeCheck, Wallet, Plus, Search, Trash2,
  ImagePlus, X as XIcon, Check, XCircle, CircleDollarSign, Receipt,
  ClipboardList, MapPin, Calendar, Briefcase, CalendarDays, Clock3,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

// ---------- Meta ----------
const EXPENSE_KINDS = [
  { key: "travel", label: "Travel (TA)",    icon: Briefcase,  color: "#3B82F6" },
  { key: "daily",  label: "Daily (DA)",     icon: Calendar,   color: "#10B981" },
  { key: "office", label: "Office",         icon: Building2,  color: "#F59E0B" },
  { key: "other",  label: "Other",          icon: ClipboardList, color: "#8B5CF6" },
];
const EXPENSE_KIND_MAP = Object.fromEntries(EXPENSE_KINDS.map((k) => [k.key, k]));

const EXPENSE_STATUSES = [
  { key: "pending",  label: "Pending",  color: "#F59E0B" },
  { key: "approved", label: "Approved", color: "#3B82F6" },
  { key: "rejected", label: "Rejected", color: "#EF4444" },
  { key: "paid",     label: "Paid",     color: "#10B981" },
];
const EXPENSE_STATUS_MAP = Object.fromEntries(EXPENSE_STATUSES.map((s) => [s.key, s]));

const fmtINR = (v) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(v || 0);
const fmtDate = (s) => { if (!s) return "—"; try { return new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return s; } };

// ---------- Page ----------
export default function HRManagement() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "directory";
  const setTab = (t) => setParams({ tab: t });

  const tabs = [
    { key: "directory",    label: "Team Directory", icon: Users },
    { key: "departments",  label: "Departments",    icon: Building2 },
    { key: "designations", label: "Designations",   icon: BadgeCheck },
    { key: "leave",        label: "Leave",          icon: CalendarDays },
    { key: "expenses",     label: "Team Expenses",  icon: Wallet },
  ];

  return (
    <div className="space-y-4" data-testid="hr-management">
      <div>
        <h1 className="text-2xl font-semibold font-display">HR Management</h1>
        <p className="text-sm text-muted-foreground">Team directory, department structure, designations and reimbursable expenses.</p>
      </div>
      <div className="border-b border-border/60 flex gap-1 overflow-x-auto">
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`hr-tab-${t.key}`}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${active ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            >
              <Icon className="w-3.5 h-3.5" />{t.label}
            </button>
          );
        })}
      </div>
      {tab === "directory"    && <DirectoryTab />}
      {tab === "departments"  && <DepartmentsTab />}
      {tab === "designations" && <DesignationsTab />}
      {tab === "leave"        && <LeaveTab />}
      {tab === "expenses"     && <ExpensesTab />}
    </div>
  );
}

// ==================== DIRECTORY ====================
function DirectoryTab() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [deptFilter, setDeptFilter] = useState("all");
  const [depts, setDepts] = useState([]);
  const [desigs, setDesigs] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dir, d, g] = await Promise.all([
        api.get("/hr/employees/directory"),
        api.get("/hr/departments"),
        api.get("/hr/designations"),
      ]);
      setRows(dir.data || []);
      setDepts(d.data || []);
      setDesigs(g.data || []);
      setManagers((dir.data || []).filter((e) => (e.role || "").match(/manager|director|admin/i)));
    } catch { toast.error("Failed to load directory"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (deptFilter !== "all" && r.department !== deptFilter && r.department_name !== deptFilter) return false;
    if (!q.trim()) return true;
    const n = q.toLowerCase();
    return (r.name || "").toLowerCase().includes(n)
      || (r.email || "").toLowerCase().includes(n)
      || (r.employee_code || "").toLowerCase().includes(n)
      || (r.designation_title || r.designation || "").toLowerCase().includes(n);
  }), [rows, q, deptFilter]);

  const distinctDepts = useMemo(() => {
    const set = new Set(rows.map((r) => r.department_name || r.department).filter(Boolean));
    return Array.from(set).sort();
  }, [rows]);

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-border/60 bg-card px-3 py-2 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, code, email, designation…" className="pl-8 h-8 text-xs" data-testid="dir-search" />
        </div>
        <Select value={deptFilter} onValueChange={setDeptFilter}>
          <SelectTrigger className="h-8 text-xs w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All departments</SelectItem>
            {distinctDepts.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {loading && <div className="rounded-2xl border border-border/60 bg-card p-12 text-center text-muted-foreground">Loading…</div>}
      {!loading && (
        <div className="rounded-2xl border border-border/60 bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">Code</th>
                <th className="text-left">Name</th>
                <th className="text-left">Designation</th>
                <th className="text-left">Department</th>
                <th className="text-left">Manager</th>
                <th className="text-left">Contact</th>
                <th className="text-left">Joined</th>
                <th className="text-right pr-3">Salary (CTC)</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-muted-foreground text-xs">No employees match.</td></tr>}
              {filtered.map((r) => (
                <tr key={r.id} data-testid={`dir-row-${r.id}`} className="border-t border-border/50 hover:bg-muted/20 cursor-pointer" onClick={() => setEditing(r)}>
                  <td className="px-3 py-2 text-[11px] font-mono text-muted-foreground">{r.employee_code || "—"}</td>
                  <td>
                    <div className="font-medium">{r.name}</div>
                    <div className="text-[10px] text-muted-foreground">{r.email || ""}</div>
                  </td>
                  <td className="text-xs">{r.designation_title || r.designation || "—"}</td>
                  <td className="text-xs">{r.department_name || r.department || "—"}</td>
                  <td className="text-xs text-muted-foreground">{r.manager_name || "—"}</td>
                  <td className="text-xs text-muted-foreground">{r.phone || "—"}</td>
                  <td className="text-xs text-muted-foreground">{fmtDate(r.date_of_joining || r.join_date)}</td>
                  <td className="text-right pr-3 text-xs">{r.salary ? fmtINR(r.salary) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <EmployeeEditDialog
        open={!!editing}
        emp={editing}
        depts={depts}
        desigs={desigs}
        managers={rows}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    </div>
  );
}

function EmployeeEditDialog({ open, emp, depts, desigs, managers, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open || !emp) return;
    setForm({
      name: emp.name || "", email: emp.email || "", phone: emp.phone || "",
      role: emp.role || "", status: emp.status || "active",
      employee_code: emp.employee_code || "", designation: emp.designation || "", department: emp.department || "",
      manager_id: emp.manager_id || "",
      date_of_birth: emp.date_of_birth || "", date_of_joining: emp.date_of_joining || emp.join_date || "",
      pan: emp.pan || "", bank_account: emp.bank_account || "", bank_ifsc: emp.bank_ifsc || "",
      address: emp.address || "",
      emergency_contact_name: emp.emergency_contact_name || "", emergency_contact_phone: emp.emergency_contact_phone || "",
      salary: emp.salary || 0,
    });
  }, [open, emp]);
  if (!emp) return null;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/employees/${emp.id}`, { ...form, salary: Number(form.salary) || 0 });
      toast.success("Employee updated");
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const genCode = async () => {
    try {
      const { data } = await api.post(`/hr/employees/${emp.id}/generate-code`);
      setForm((f) => ({ ...f, employee_code: data.employee_code }));
      toast.success(`Code assigned: ${data.employee_code}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit employee</DialogTitle>
          <DialogDescription>Profile, contact, employment, banking and emergency details.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Employee Code</Label>
            <div className="flex gap-1">
              <Input value={form.employee_code || ""} onChange={(e) => setForm({ ...form, employee_code: e.target.value })} data-testid="emp-code" placeholder="EMP-0001" />
              {!form.employee_code && <Button size="sm" variant="outline" onClick={genCode}>Generate</Button>}
            </div>
          </div>
          <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="emp-name" /></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          <div><Label>Department</Label>
            <Select value={form.department || "__none__"} onValueChange={(v) => setForm({ ...form, department: v === "__none__" ? "" : v })}>
              <SelectTrigger data-testid="emp-dept"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">—</SelectItem>
                {depts.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Designation</Label>
            <Select value={form.designation || "__none__"} onValueChange={(v) => setForm({ ...form, designation: v === "__none__" ? "" : v })}>
              <SelectTrigger data-testid="emp-desig"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">—</SelectItem>
                {desigs.map((d) => <SelectItem key={d.id} value={d.title}>{d.title} {d.grade ? `(${d.grade})` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Manager</Label>
            <Select value={form.manager_id || "__none__"} onValueChange={(v) => setForm({ ...form, manager_id: v === "__none__" ? "" : v })}>
              <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">—</SelectItem>
                {managers.filter((m) => m.id !== emp.id).map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Role</Label>
            <Select value={form.role || ""} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["super_admin","director","hr","accounts","manager","project_manager","architect","site_engineer","designer","sales","employee","client"].map((r) => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Status</Label>
            <Select value={form.status || "active"} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="on_leave">On Leave</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Salary (CTC/annum)</Label><Input type="number" value={form.salary || 0} onChange={(e) => setForm({ ...form, salary: e.target.value })} data-testid="emp-salary" /></div>
          <div><Label>Date of Joining</Label><Input type="date" value={form.date_of_joining || ""} onChange={(e) => setForm({ ...form, date_of_joining: e.target.value })} /></div>
          <div><Label>Date of Birth</Label><Input type="date" value={form.date_of_birth || ""} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} /></div>
          <div><Label>PAN</Label><Input value={form.pan || ""} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} placeholder="ABCDE1234F" maxLength={10} /></div>
          <div><Label>Bank Account No.</Label><Input value={form.bank_account || ""} onChange={(e) => setForm({ ...form, bank_account: e.target.value })} /></div>
          <div><Label>IFSC Code</Label><Input value={form.bank_ifsc || ""} onChange={(e) => setForm({ ...form, bank_ifsc: e.target.value.toUpperCase() })} placeholder="HDFC0001234" /></div>
          <div className="col-span-2"><Label>Address</Label><Textarea rows={2} value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
          <div><Label>Emergency contact — name</Label><Input value={form.emergency_contact_name || ""} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })} /></div>
          <div><Label>Emergency contact — phone</Label><Input value={form.emergency_contact_phone || ""} onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })} /></div>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground" data-testid="emp-save">{saving ? "Saving…" : "Save"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ==================== DEPARTMENTS ====================
function DepartmentsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hr/departments");
      setRows(data || []);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (row) => {
    if (!confirm(`Delete department "${row.name}"?`)) return;
    try {
      await api.delete(`/hr/departments/${row.id}`);
      toast.success("Deleted"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">{rows.length} departments</div>
        <Button size="sm" onClick={() => setCreating(true)} className="bg-primary text-primary-foreground" data-testid="dept-new"><Plus className="w-3.5 h-3.5 mr-1" />New Department</Button>
      </div>
      <div className="rounded-2xl border border-border/60 bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left">Description</th>
              <th className="w-24"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={3} className="text-center py-10 text-muted-foreground text-xs">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={3} className="text-center py-10 text-muted-foreground text-xs">No departments yet.</td></tr>}
            {!loading && rows.map((r) => (
              <tr key={r.id} className="border-t border-border/50 hover:bg-muted/20">
                <td className="px-3 py-2 font-medium">{r.name}</td>
                <td className="text-xs text-muted-foreground">{r.description || "—"}</td>
                <td className="text-right pr-3">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(r)}>Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(r)} className="text-rose-500"><Trash2 className="w-3.5 h-3.5" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <LookupDialog
        open={!!editing || creating}
        row={editing}
        kind="dept"
        onClose={() => { setEditing(null); setCreating(false); }}
        onSaved={() => { setEditing(null); setCreating(false); load(); }}
      />
    </div>
  );
}

// ==================== DESIGNATIONS ====================
function DesignationsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/hr/designations"); setRows(data || []); }
    catch { toast.error("Failed"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (row) => {
    if (!confirm(`Delete designation "${row.title}"?`)) return;
    try { await api.delete(`/hr/designations/${row.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">{rows.length} designations</div>
        <Button size="sm" onClick={() => setCreating(true)} className="bg-primary text-primary-foreground" data-testid="desig-new"><Plus className="w-3.5 h-3.5 mr-1" />New Designation</Button>
      </div>
      <div className="rounded-2xl border border-border/60 bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2">Title</th>
              <th className="text-left w-24">Grade</th>
              <th className="text-left">Description</th>
              <th className="w-24"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={4} className="text-center py-10 text-muted-foreground text-xs">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={4} className="text-center py-10 text-muted-foreground text-xs">No designations yet.</td></tr>}
            {!loading && rows.map((r) => (
              <tr key={r.id} className="border-t border-border/50 hover:bg-muted/20">
                <td className="px-3 py-2 font-medium">{r.title}</td>
                <td className="text-xs text-muted-foreground">{r.grade || "—"}</td>
                <td className="text-xs text-muted-foreground">{r.description || "—"}</td>
                <td className="text-right pr-3">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(r)}>Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(r)} className="text-rose-500"><Trash2 className="w-3.5 h-3.5" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <LookupDialog
        open={!!editing || creating}
        row={editing}
        kind="desig"
        onClose={() => { setEditing(null); setCreating(false); }}
        onSaved={() => { setEditing(null); setCreating(false); load(); }}
      />
    </div>
  );
}

function LookupDialog({ open, row, kind, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setForm(row ? { ...row } : (kind === "dept" ? { name: "", description: "" } : { title: "", grade: "", description: "" }));
  }, [open, row, kind]);
  const save = async () => {
    setSaving(true);
    try {
      const url = kind === "dept" ? "/hr/departments" : "/hr/designations";
      if (row) await api.patch(`${url}/${row.id}`, form);
      else await api.post(url, form);
      toast.success("Saved");
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{row ? "Edit" : "New"} {kind === "dept" ? "department" : "designation"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {kind === "dept" ? (
            <div><Label>Name</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="lookup-name" /></div>
          ) : (
            <>
              <div><Label>Title</Label><Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="lookup-name" /></div>
              <div><Label>Grade</Label><Input value={form.grade || ""} onChange={(e) => setForm({ ...form, grade: e.target.value })} placeholder="e.g. L2, L3" /></div>
            </>
          )}
          <div><Label>Description</Label><Textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground" data-testid="lookup-save">{saving ? "Saving…" : "Save"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ==================== EXPENSES ====================
function ExpensesTab() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [fStatus, setFStatus] = useState("all");
  const [fKind, setFKind] = useState("all");
  const [creating, setCreating] = useState(false);
  const [openExp, setOpenExp] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, s, emps, prj] = await Promise.all([
        api.get("/hr/expenses"),
        api.get("/hr/expenses/summary"),
        api.get("/employees").catch(() => ({ data: [] })),
        api.get("/projects").catch(() => ({ data: [] })),
      ]);
      setRows(e.data || []);
      setSummary(s.data);
      setEmployees(emps.data || []);
      setProjects(prj.data || []);
    } catch { toast.error("Failed to load expenses"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (fStatus !== "all" && r.status !== fStatus) return false;
    if (fKind !== "all" && r.kind !== fKind) return false;
    if (!q.trim()) return true;
    const n = q.toLowerCase();
    return (r.expense_no || "").toLowerCase().includes(n)
      || (r.employee_name || "").toLowerCase().includes(n)
      || (r.description || "").toLowerCase().includes(n);
  }), [rows, q, fStatus, fKind]);

  return (
    <div className="space-y-3">
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {EXPENSE_STATUSES.map((st) => (
            <div key={st.key} className="rounded-xl border border-border/60 bg-card px-3 py-2">
              <div className="text-[9px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: st.color }} />
                {st.label}
              </div>
              <div className="text-lg font-semibold font-display mt-0.5">{summary.by_status?.[st.key]?.count || 0}</div>
              <div className="text-[10px] text-muted-foreground">{fmtINR(summary.by_status?.[st.key]?.amount || 0)}</div>
            </div>
          ))}
        </div>
      )}
      <div className="rounded-xl border border-border/60 bg-card px-3 py-2 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search expense no, employee, description…" className="pl-8 h-8 text-xs" data-testid="exp-search" />
        </div>
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger className="h-8 text-xs w-36" data-testid="exp-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {EXPENSE_STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fKind} onValueChange={setFKind}>
          <SelectTrigger className="h-8 text-xs w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All kinds</SelectItem>
            {EXPENSE_KINDS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" onClick={() => setCreating(true)} className="bg-primary text-primary-foreground" data-testid="exp-new"><Plus className="w-3.5 h-3.5 mr-1" />Submit Expense</Button>
      </div>
      <div className="rounded-2xl border border-border/60 bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2">No.</th>
              <th className="text-left">Employee</th>
              <th className="text-left">Kind</th>
              <th className="text-left">Date</th>
              <th className="text-left">Description</th>
              <th className="text-right">Amount</th>
              <th className="text-left w-28">Status</th>
              <th className="text-center w-12">📎</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="text-center py-10 text-muted-foreground text-xs">Loading…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-muted-foreground text-xs">No expenses match.</td></tr>}
            {!loading && filtered.map((r) => {
              const K = EXPENSE_KIND_MAP[r.kind];
              const S = EXPENSE_STATUS_MAP[r.status];
              const Icon = K?.icon || ClipboardList;
              return (
                <tr key={r.id} data-testid={`exp-row-${r.id}`} className="border-t border-border/50 hover:bg-muted/20 cursor-pointer" onClick={() => setOpenExp(r)}>
                  <td className="px-3 py-2 text-[11px] font-mono text-muted-foreground">{r.expense_no}</td>
                  <td>
                    <div className="text-sm font-medium">{r.employee_name || "—"}</div>
                    <div className="text-[10px] text-muted-foreground">{r.employee_code || ""}</div>
                  </td>
                  <td>
                    <span className="inline-flex items-center gap-1 text-[10px]" style={{ color: K?.color }}>
                      <Icon className="w-3 h-3" /> {K?.label || r.kind}
                    </span>
                  </td>
                  <td className="text-xs text-muted-foreground">{fmtDate(r.incurred_on)}</td>
                  <td className="text-xs text-muted-foreground truncate max-w-[240px]">{r.description || "—"}</td>
                  <td className="text-right font-medium">{fmtINR(r.amount)}</td>
                  <td>
                    <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border" style={{ color: S?.color, borderColor: S?.color + "55", background: S?.color + "12" }}>{S?.label}</span>
                  </td>
                  <td className="text-center text-xs text-muted-foreground">{r.receipt_count > 0 ? `${r.receipt_count}` : ""}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <ExpenseCreateDialog open={creating} employees={employees} projects={projects} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />
      <ExpenseDetailDialog open={!!openExp} expenseId={openExp?.id} onClose={() => setOpenExp(null)} onChanged={load} />
    </div>
  );
}

function ExpenseCreateDialog({ open, employees, projects, onClose, onSaved }) {
  const { user } = useAuth();
  const [form, setForm] = useState({});
  const [receipts, setReceipts] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    // preselect employee = current user's employee row if we can find it
    const mine = (employees || []).find((e) => (e.email || "").toLowerCase() === (user?.email || "").toLowerCase());
    setForm({
      employee_id: mine?.id || (employees[0]?.id || ""),
      kind: "travel",
      amount: "",
      incurred_on: new Date().toISOString().slice(0, 10),
      project_id: "",
      description: "",
    });
    setReceipts([]);
  }, [open, employees, user]);
  const pickPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) {
      if (f.size > 2 * 1024 * 1024) { toast.error(`${f.name} exceeds 2 MB`); continue; }
      if (receipts.length >= 6) { toast.error("Max 6 receipts"); break; }
      const url = await new Promise((r) => { const rd = new FileReader(); rd.onload = () => r(rd.result); rd.readAsDataURL(f); });
      setReceipts((prev) => [...prev, { data_url: url, caption: f.name }]);
    }
    e.target.value = "";
  };
  const save = async () => {
    if (!form.employee_id) return toast.error("Employee required");
    const amt = Number(form.amount);
    if (!amt || amt <= 0) return toast.error("Amount must be > 0");
    setSaving(true);
    try {
      await api.post("/hr/expenses", {
        ...form,
        amount: amt,
        project_id: form.project_id || null,
        receipts,
      });
      toast.success("Expense submitted");
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Receipt className="w-4 h-4 text-primary" /> Submit expense</DialogTitle>
          <DialogDescription>Attach receipts. Approval routes to your manager, then Accounts marks it Paid.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Employee</Label>
            <Select value={form.employee_id || ""} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
              <SelectTrigger data-testid="exp-emp"><SelectValue placeholder="Pick…" /></SelectTrigger>
              <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Kind</Label>
            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
              <SelectTrigger data-testid="exp-kind"><SelectValue /></SelectTrigger>
              <SelectContent>{EXPENSE_KINDS.map((k) => <SelectItem key={k.key} value={k.key}>{k.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Amount (₹)</Label><Input type="number" value={form.amount || ""} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="exp-amount" /></div>
          <div><Label>Date incurred</Label><Input type="date" value={form.incurred_on || ""} onChange={(e) => setForm({ ...form, incurred_on: e.target.value })} data-testid="exp-date" /></div>
          <div className="col-span-2"><Label>Project (optional)</Label>
            <Select value={form.project_id || "__none__"} onValueChange={(v) => setForm({ ...form, project_id: v === "__none__" ? "" : v })}>
              <SelectTrigger><SelectValue placeholder="No project" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— No project —</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Description</Label><Textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Client visit to Bandra, cab fare + parking" data-testid="exp-desc" /></div>
          <div className="col-span-2">
            <Label>Receipts ({receipts.length}/6)</Label>
            <div className="rounded-lg border border-dashed border-border/70 p-3">
              <label className="flex items-center justify-center gap-2 cursor-pointer py-3 rounded-md hover:bg-muted/30 transition">
                <ImagePlus className="w-4 h-4 text-primary" />
                <span className="text-xs">Add receipts / bills (≤ 2 MB each)</span>
                <input type="file" accept="image/*,application/pdf" multiple onChange={pickPhotos} className="hidden" data-testid="exp-receipt-picker" />
              </label>
              {receipts.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {receipts.map((p, i) => (
                    <div key={i} className="relative rounded overflow-hidden border border-border/50 group">
                      <img src={p.data_url} alt={p.caption} className="w-full h-16 object-cover" />
                      <button type="button" onClick={() => setReceipts((prev) => prev.filter((_, x) => x !== i))} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100">
                        <XIcon className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground" data-testid="exp-submit">{saving ? "Submitting…" : "Submit"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ExpenseDetailDialog({ open, expenseId, onClose, onChanged }) {
  const { user } = useAuth();
  const [exp, setExp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [payForm, setPayForm] = useState({ payment_method: "bank_transfer", reference: "", account_code: "6100" });
  const [showPay, setShowPay] = useState(false);

  const load = useCallback(async () => {
    if (!expenseId) return;
    setLoading(true);
    try { const { data } = await api.get(`/hr/expenses/${expenseId}`); setExp(data); }
    catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [expenseId]);
  useEffect(() => { if (open) { load(); setNote(""); setShowPay(false); } }, [open, load]);

  const act = async (endpoint, body) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/hr/expenses/${exp.id}/${endpoint}`, body || {});
      setExp(data);
      toast.success(`Marked ${data.status}`);
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || `Failed to ${endpoint}`); }
    finally { setBusy(false); }
  };

  const removeReceipt = async (rid) => {
    setBusy(true);
    try { await api.delete(`/hr/expenses/${exp.id}/receipts/${rid}`); await load(); onChanged && onChanged(); }
    catch { toast.error("Failed to delete receipt"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!confirm(`Delete ${exp.expense_no}? This cannot be undone.`)) return;
    setBusy(true);
    try { await api.delete(`/hr/expenses/${exp.id}`); toast.success("Deleted"); onChanged && onChanged(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    finally { setBusy(false); }
  };

  if (!open) return null;
  const canApprove = ["super_admin", "hr", "director", "manager", "project_manager"].includes(user?.role);
  const canPay = ["super_admin", "hr", "accounts", "finance", "director"].includes(user?.role);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        {loading || !exp ? (
          <div className="text-center py-16 text-muted-foreground">Loading…</div>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest">{exp.expense_no}</div>
                  <DialogTitle className="text-lg leading-tight flex items-center gap-2 mt-0.5">
                    <CircleDollarSign className="w-4 h-4 text-primary" />
                    {fmtINR(exp.amount)} · {EXPENSE_KIND_MAP[exp.kind]?.label || exp.kind}
                  </DialogTitle>
                  <DialogDescription className="flex items-center gap-2 mt-1">
                    <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border" style={{ color: EXPENSE_STATUS_MAP[exp.status]?.color, borderColor: EXPENSE_STATUS_MAP[exp.status]?.color + "55", background: EXPENSE_STATUS_MAP[exp.status]?.color + "12" }}>
                      {EXPENSE_STATUS_MAP[exp.status]?.label}
                    </span>
                    <span className="text-xs text-muted-foreground">by {exp.employee_name || "—"}</span>
                    <span className="text-xs text-muted-foreground">· {fmtDate(exp.incurred_on)}</span>
                  </DialogDescription>
                </div>
                {exp.status !== "paid" && <Button size="sm" variant="ghost" onClick={remove} className="text-rose-500"><Trash2 className="w-4 h-4" /></Button>}
              </div>
            </DialogHeader>

            {exp.description && <div className="text-sm text-muted-foreground whitespace-pre-wrap border-y border-border/40 py-3 -mx-6 px-6">{exp.description}</div>}

            {/* Lifecycle timeline */}
            <div className="text-xs space-y-1 border-b border-border/40 pb-3">
              <div>Submitted · <span className="text-muted-foreground">{fmtDate(exp.created_at)}</span></div>
              {exp.approved_on && <div>Approved · <span className="text-muted-foreground">{fmtDate(exp.approved_on)}{exp.approval_note ? ` — “${exp.approval_note}”` : ""}</span></div>}
              {exp.rejected_on && <div className="text-rose-500">Rejected · {fmtDate(exp.rejected_on)}{exp.rejection_note ? ` — “${exp.rejection_note}”` : ""}</div>}
              {exp.paid_on && <div className="text-emerald-500">Paid · {fmtDate(exp.paid_on)} via {exp.payment_method} {exp.payment_reference ? ` (${exp.payment_reference})` : ""}</div>}
              {exp.accounting_entry_id && <div className="text-[10px] text-muted-foreground">Journal entry: {exp.accounting_entry_id.slice(0, 8)}…</div>}
            </div>

            {/* Receipts */}
            <div>
              <div className="text-sm font-medium mb-2 flex items-center gap-1"><Receipt className="w-3.5 h-3.5 text-primary" /> Receipts · {exp.receipts?.length || 0}</div>
              {exp.receipts?.length > 0 ? (
                <div className="grid grid-cols-4 gap-2">
                  {exp.receipts.map((p) => (
                    <div key={p.id} className="relative rounded overflow-hidden border border-border/50 group">
                      <a href={p.data_url} target="_blank" rel="noreferrer"><img src={p.data_url} alt={p.caption} className="w-full h-24 object-cover cursor-zoom-in" /></a>
                      {exp.status !== "paid" && (
                        <button onClick={() => removeReceipt(p.id)} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100"><XIcon className="w-3 h-3" /></button>
                      )}
                    </div>
                  ))}
                </div>
              ) : <div className="text-xs text-muted-foreground italic">No receipts attached.</div>}
            </div>

            {/* Actions */}
            {exp.status === "pending" && canApprove && (
              <div className="rounded-lg border border-border/60 bg-muted/20 p-3 space-y-2">
                <Label className="text-xs">Approver note (required for reject)</Label>
                <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason or remarks…" data-testid="exp-note" />
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => act("reject", { note })} disabled={busy || !note.trim()} data-testid="exp-reject" className="text-rose-500 border-rose-500/40"><XCircle className="w-3.5 h-3.5 mr-1" />Reject</Button>
                  <Button size="sm" onClick={() => act("approve", { note })} disabled={busy} className="bg-primary text-primary-foreground" data-testid="exp-approve"><Check className="w-3.5 h-3.5 mr-1" />Approve</Button>
                </div>
              </div>
            )}
            {exp.status === "approved" && canPay && !showPay && (
              <div className="flex justify-end">
                <Button size="sm" onClick={() => setShowPay(true)} className="bg-emerald-600 text-white" data-testid="exp-pay-open">Mark Paid…</Button>
              </div>
            )}
            {exp.status === "approved" && showPay && (
              <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-3 space-y-2">
                <div className="text-xs font-medium text-emerald-600">Post to Accounts</div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-[11px]">Method</Label>
                    <Select value={payForm.payment_method} onValueChange={(v) => setPayForm({ ...payForm, payment_method: v })}>
                      <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                        <SelectItem value="upi">UPI</SelectItem>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="cheque">Cheque</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-[11px]">Reference</Label><Input value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} placeholder="NEFT ref / UTR" data-testid="exp-pay-ref" /></div>
                  <div><Label className="text-[11px]">Account code</Label><Input value={payForm.account_code} onChange={(e) => setPayForm({ ...payForm, account_code: e.target.value })} placeholder="6100" /></div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setShowPay(false)}>Cancel</Button>
                  <Button size="sm" onClick={() => act("pay", payForm)} disabled={busy} className="bg-emerald-600 text-white" data-testid="exp-pay-confirm">Confirm & Post</Button>
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ==================== LEAVE (HR-B) ====================
const LEAVE_STATUSES = [
  { key: "pending",   label: "Pending",   color: "#F59E0B" },
  { key: "approved",  label: "Approved",  color: "#10B981" },
  { key: "rejected",  label: "Rejected",  color: "#EF4444" },
  { key: "cancelled", label: "Cancelled", color: "#64748B" },
];
const LEAVE_STATUS_MAP = Object.fromEntries(LEAVE_STATUSES.map((s) => [s.key, s]));

function LeaveTab() {
  const { user } = useAuth();
  const [types, setTypes] = useState([]);
  const [balances, setBalances] = useState([]);
  const [requests, setRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [fStatus, setFStatus] = useState("all");
  const [creating, setCreating] = useState(false);
  const [openReq, setOpenReq] = useState(null);
  const [empFilter, setEmpFilter] = useState("me");   // "me" | "all" | employee_id

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, e] = await Promise.all([
        api.get("/hr/leave/types"),
        api.get("/employees").catch(() => ({ data: [] })),
      ]);
      setTypes(t.data || []);
      setEmployees(e.data || []);
      // Determine "my" employee row
      const mine = (e.data || []).find((x) => (x.email || "").toLowerCase() === (user?.email || "").toLowerCase());
      const balUrl = mine ? `/hr/leave/balances?employee_id=${mine.id}` : "/hr/leave/balances";
      const reqUrl = empFilter === "me" && mine ? `/hr/leave/requests?employee_id=${mine.id}`
                    : empFilter !== "me" && empFilter !== "all" ? `/hr/leave/requests?employee_id=${empFilter}`
                    : "/hr/leave/requests";
      const [b, r] = await Promise.all([
        api.get(balUrl),
        api.get(reqUrl),
      ]);
      setBalances(b.data || []);
      setRequests(r.data || []);
    } catch { toast.error("Failed to load leave data"); }
    finally { setLoading(false); }
  }, [user, empFilter]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => requests.filter((r) => {
    if (fStatus !== "all" && r.status !== fStatus) return false;
    if (!q.trim()) return true;
    const n = q.toLowerCase();
    return (r.employee_name || "").toLowerCase().includes(n)
      || (r.reason || "").toLowerCase().includes(n)
      || (r.leave_type_code || "").toLowerCase().includes(n);
  }), [requests, q, fStatus]);

  const isApprover = ["super_admin", "hr", "director", "manager", "project_manager"].includes(user?.role);

  return (
    <div className="space-y-3">
      {/* My Balances */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {balances.map((b) => (
          <div key={b.leave_type_id} className="rounded-xl border border-border/60 bg-card px-3 py-2">
            <div className="text-[9px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: b.color }} />
              {b.code} · {b.name}
            </div>
            <div className="text-2xl font-semibold font-display mt-0.5">
              {b.unlimited ? "∞" : (b.remaining ?? 0)}
              <span className="text-[10px] text-muted-foreground ml-1 font-normal">
                {b.unlimited ? "unlimited" : `/ ${b.entitled} days left`}
              </span>
            </div>
            <div className="text-[10px] text-muted-foreground">
              Used {b.used} {b.pending ? `· ${b.pending} pending` : ""}
            </div>
          </div>
        ))}
      </div>
      {/* Toolbar */}
      <div className="rounded-xl border border-border/60 bg-card px-3 py-2 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search employee, type, reason…" className="pl-8 h-8 text-xs" data-testid="leave-search" />
        </div>
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger className="h-8 text-xs w-36" data-testid="leave-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {LEAVE_STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {isApprover && (
          <Select value={empFilter} onValueChange={setEmpFilter}>
            <SelectTrigger className="h-8 text-xs w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="me">My leave</SelectItem>
              <SelectItem value="all">All employees</SelectItem>
              {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Button size="sm" onClick={() => setCreating(true)} className="bg-primary text-primary-foreground" data-testid="leave-new"><Plus className="w-3.5 h-3.5 mr-1" />Apply</Button>
      </div>
      {/* Table */}
      <div className="rounded-2xl border border-border/60 bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2">Employee</th>
              <th className="text-left">Type</th>
              <th className="text-left">From → To</th>
              <th className="text-left w-20">Days</th>
              <th className="text-left">Reason</th>
              <th className="text-left w-28">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="text-center py-10 text-muted-foreground text-xs">Loading…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-muted-foreground text-xs">No leave requests match.</td></tr>}
            {!loading && filtered.map((r) => {
              const S = LEAVE_STATUS_MAP[r.status];
              return (
                <tr key={r.id} data-testid={`leave-row-${r.id}`} className="border-t border-border/50 hover:bg-muted/20 cursor-pointer" onClick={() => setOpenReq(r)}>
                  <td className="px-3 py-2">
                    <div className="text-sm font-medium">{r.employee_name || "—"}</div>
                    <div className="text-[10px] text-muted-foreground">{r.employee_code || ""}</div>
                  </td>
                  <td>
                    <span className="inline-flex items-center gap-1 text-[10px]" style={{ color: r.leave_type_color }}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: r.leave_type_color }} />
                      {r.leave_type_code}
                    </span>
                  </td>
                  <td className="text-xs text-muted-foreground">{fmtDate(r.start_date)} → {fmtDate(r.end_date)}</td>
                  <td className="text-xs font-medium">{r.days}{r.half_day ? " (½)" : ""}</td>
                  <td className="text-xs text-muted-foreground truncate max-w-[240px]">{r.reason || "—"}</td>
                  <td>
                    <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border" style={{ color: S?.color, borderColor: S?.color + "55", background: S?.color + "12" }}>{S?.label}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <LeaveCreateDialog open={creating} types={types} employees={employees} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />
      <LeaveDetailDialog open={!!openReq} rid={openReq?.id} onClose={() => setOpenReq(null)} onChanged={load} isApprover={isApprover} />
    </div>
  );
}

function LeaveCreateDialog({ open, types, employees, onClose, onSaved }) {
  const { user } = useAuth();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    const mine = (employees || []).find((e) => (e.email || "").toLowerCase() === (user?.email || "").toLowerCase());
    setForm({
      employee_id: mine?.id || (employees[0]?.id || ""),
      leave_type_id: types[0]?.id || "",
      start_date: new Date().toISOString().slice(0, 10),
      end_date: new Date().toISOString().slice(0, 10),
      half_day: false,
      reason: "",
    });
  }, [open, employees, types, user]);

  const save = async () => {
    if (!form.leave_type_id) return toast.error("Pick a leave type");
    setSaving(true);
    try {
      await api.post("/hr/leave/requests", form);
      toast.success("Leave applied");
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarDays className="w-4 h-4 text-primary" /> Apply for leave</DialogTitle>
          <DialogDescription>Balance is deducted only when your manager approves.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Employee</Label>
            <Select value={form.employee_id || ""} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
              <SelectTrigger data-testid="leave-emp"><SelectValue /></SelectTrigger>
              <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Leave type</Label>
            <Select value={form.leave_type_id || ""} onValueChange={(v) => setForm({ ...form, leave_type_id: v })}>
              <SelectTrigger data-testid="leave-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {types.map((t) => <SelectItem key={t.id} value={t.id}>{t.code} — {t.name} {t.entitled_days ? `(${t.entitled_days}d)` : "(unlimited)"}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>From</Label><Input type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="leave-start" /></div>
          <div><Label>To</Label><Input type="date" value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="leave-end" /></div>
          <div className="col-span-2">
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={!!form.half_day} onChange={(e) => setForm({ ...form, half_day: e.target.checked })} className="w-4 h-4 accent-primary" data-testid="leave-half" />
              Half-day (only for a single-day leave)
            </label>
          </div>
          <div className="col-span-2"><Label>Reason</Label><Textarea rows={2} value={form.reason || ""} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="leave-reason" /></div>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground" data-testid="leave-submit">{saving ? "Applying…" : "Apply"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LeaveDetailDialog({ open, rid, isApprover, onClose, onChanged }) {
  const { user } = useAuth();
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    if (!rid) return;
    setLoading(true);
    try {
      const [{ data }, all] = await Promise.all([
        api.get(`/hr/leave/requests/${rid}`),
        api.get("/hr/leave/requests"),
      ]);
      const enriched = (all.data || []).find((x) => x.id === rid);
      setR(enriched || data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [rid]);
  useEffect(() => { if (open) { load(); setNote(""); } }, [open, load]);

  const act = async (endpoint, body) => {
    setBusy(true);
    try {
      await api.post(`/hr/leave/requests/${rid}/${endpoint}`, body || {});
      toast.success(`Marked ${endpoint}`);
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || `Failed to ${endpoint}`); }
    finally { setBusy(false); }
  };
  const cancel = async () => {
    if (!confirm("Cancel this leave request?")) return;
    setBusy(true);
    try {
      await api.delete(`/hr/leave/requests/${rid}`);
      toast.success("Cancelled");
      onChanged && onChanged();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!open) return null;
  const S = r ? LEAVE_STATUS_MAP[r.status] : null;
  const isMine = r && ((r.employee_name || "") && ((user?.email || "") !== "")); // approximate — cancel is server-guarded anyway
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        {loading || !r ? (
          <div className="text-center py-16 text-muted-foreground">Loading…</div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-primary" />
                {r.leave_type_code} — {r.days} day{r.days > 1 ? "s" : ""}{r.half_day ? " (½)" : ""}
              </DialogTitle>
              <DialogDescription className="flex items-center gap-2 mt-1">
                <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border" style={{ color: S?.color, borderColor: S?.color + "55", background: S?.color + "12" }}>{S?.label}</span>
                <span className="text-xs text-muted-foreground">{r.employee_name} · {fmtDate(r.start_date)} → {fmtDate(r.end_date)}</span>
              </DialogDescription>
            </DialogHeader>
            {r.reason && <div className="text-sm text-muted-foreground whitespace-pre-wrap border-y border-border/40 py-2 -mx-6 px-6">{r.reason}</div>}
            <div className="text-xs space-y-1">
              <div>Submitted · <span className="text-muted-foreground">{fmtDate(r.created_at)}</span></div>
              {r.approved_on && <div className="text-emerald-500">Approved · {fmtDate(r.approved_on)}{r.approval_note ? ` — “${r.approval_note}”` : ""}</div>}
              {r.rejected_on && <div className="text-rose-500">Rejected · {fmtDate(r.rejected_on)}{r.rejection_note ? ` — “${r.rejection_note}”` : ""}</div>}
              {r.cancelled_on && <div className="text-muted-foreground">Cancelled · {fmtDate(r.cancelled_on)}</div>}
            </div>
            {r.status === "pending" && isApprover && (
              <div className="rounded-lg border border-border/60 bg-muted/20 p-3 space-y-2">
                <Label className="text-xs">Note (required for reject)</Label>
                <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="leave-note" />
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => act("reject", { note })} disabled={busy || !note.trim()} className="text-rose-500 border-rose-500/40" data-testid="leave-reject"><XCircle className="w-3.5 h-3.5 mr-1" />Reject</Button>
                  <Button size="sm" onClick={() => act("approve", { note })} disabled={busy} className="bg-primary text-primary-foreground" data-testid="leave-approve"><Check className="w-3.5 h-3.5 mr-1" />Approve</Button>
                </div>
              </div>
            )}
            {["pending", "approved"].includes(r.status) && (
              <div className="flex justify-end">
                <Button size="sm" variant="ghost" onClick={cancel} className="text-rose-500" data-testid="leave-cancel">Cancel request</Button>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

